import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState, useAppSelector } from "../../RootReducer";
import { useLocation } from "react-router";
import { getContainer } from "../../ioc/IOCSetup";
import DesktopTopMenu, { includeMenuItemForCountry } from "./DesktopTopMenu";
import { getStatusForContent } from "../../selectors/content-selectors";
import { ContentModelName, GlobalPayload, ImageObject, SideMenuContentPayload } from "../../contenful/types";
import Submenu from "./Submenu";
import { ReactNode } from "react-markdown";
import { ContentStatus } from "../../contenful/ContentfulReducer";
import { MenuItem, MenuPosition, MenuSettings } from "../../http/protocol";
import { BrandA } from "../buttons/buttons";
import { NavLink } from "react-router-dom";
import { isCasinoMenuItemActive, mapMenuItem } from "./MenuUtils";
import { openFreshChat } from "../FreshChatWidget";
import AppInstallComponent from "../AppInstallComponent";
import NavMenu, { BrandLogo } from "./NavMenu";
import { showSideMenu } from "../../navigation/NavigationActions";
import MobileBottomMenu from "./MobileBottomMenu";
import { useClickOutside } from "../../onboardingpage/modalview/utils";

import SideMenuLanguage from "./menucomponents/SideMenuLanguage";
import SideMenuContent, { HamburgerIcon, MenuCloseButton, SideMenuBottomContent, SideMenuTopContent } from "./menucomponents/SideMenuContent";
import SideMenuComponents from "./menucomponents/SideMenuComponents";
import { CopyPopOver } from "./menucomponents/SideMenuActions";

interface FixedLayoutProps {
  position?: MenuPosition;
  isExpanded?: boolean;
  children: ReactNode[] | ReactNode;
}

export default function FixedLayout(props: FixedLayoutProps) {
  const isMobile = useSelector((app: AppState) => app.settings.mobile);

  let expanded = props.isExpanded ? "expanded" : "";
  let position = props.position == MenuPosition.ABSOLUTE ? "absolute" : "fixed";

  return (
    <div className={`menu-layout ${position}`}>
      <div className={`menu-layout-navbar ${expanded}`}>{isMobile ? <NavMenu /> : <DesktopTopMenu />}</div>
      <div className={`menu-layout-sidebar ${expanded}`}>
        <FixedSideMenu position={props.position} />
      </div>
      <div className={`menu-layout-content ${expanded}`}>
        {props.children}
        {isMobile && <MobileBottomMenu />}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------------ */
/* ------------------------------------------------------------------------------------------------------ */

function FixedSideMenu(props: { position?: MenuPosition }) {
  const isLoggedIn = useSelector((app: AppState) => app.user.user?.loggedIn);
  const isExpanded = useSelector((app: AppState) => app.navigation.isSideMenuExpanded);
  const content = useSelector((app: AppState) => app.contentful.sideMenuContent);
  const country = useSelector((app: AppState) => app.user.user?.country);
  const ipCountry = useSelector((app: AppState) => app.settings.country);
  const global = useSelector((app: AppState) => app.contentful.global);
  const settings = useSelector((app: AppState) => app.settings.settings?.menu?.settings);

  const SideMenuStatus = useAppSelector((s) => getStatusForContent(s, ContentModelName.SIDE_MENU_CONTENT));

  let locale = getContainer().getSettingsService().getActiveLocale().code;
  let mainItems = getContainer().getSettingsService().getMenu().items;
  let footerItems = getContainer().getSettingsService().getMenu().footerItems;

  mainItems = mainItems?.filter((m) => includeMenuItemForCountry(m, country, ipCountry));
  footerItems = footerItems?.filter((m) => includeMenuItemForCountry(m, country, ipCountry));

  if (SideMenuStatus != ContentStatus.SUCCESS) {
    return <></>;
  } else {
    return (
      <>
        <CollapsedSidemenuContent settings={settings} global={global} content={content} mainItems={mainItems ?? []} footerItems={footerItems ?? []} isLoggedIn={isLoggedIn} isExpanded={isExpanded} locale={locale} position={props.position} />
        <ExpandedSidemenuContent settings={settings} global={global} content={content} mainItems={mainItems ?? []} footerItems={footerItems ?? []} isLoggedIn={isLoggedIn} isExpanded={isExpanded} locale={locale} position={props.position} />
      </>
    );
  }
}

/* ------------------------------------------------------------------------------------------------------ */
/* ------------------------------------------------------------------------------------------------------ */

interface SidemenuContent {
  content?: SideMenuContentPayload;
  position?: MenuPosition;
  mainItems?: MenuItem[];
  footerItems?: MenuItem[];
  isLoggedIn?: boolean;
  isExpanded?: boolean;
  locale?: string;
  global?: GlobalPayload;
  settings?: MenuSettings;
}

function ExpandedSidemenuContent(props: SidemenuContent) {
  const userLoaded = useSelector((app: AppState) => app.user.userInfoLoaded);
  const location = useLocation();
  const dispatch = useDispatch();
  const sidebarRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    dispatch(showSideMenu(false));
  };

  useClickOutside(sidebarRef, handleClick);

  const toggleSidemenu = () => {
    dispatch(showSideMenu(!props.isExpanded));
  };

  let isAbsolute = props.position == MenuPosition.ABSOLUTE;

  return (
    <div ref={isAbsolute ? sidebarRef : null} className={`sidemenu-container sidemenu-container-expanded ${props.isExpanded ? "active" : ""}`}>
      <div className={`sidemenu-header expanded`}>
        <div className={`close-button expanded ${props.settings?.enabeLogoSideMenu == true ? "logo" : ""} ` + props.position?.toLocaleLowerCase()}>{props.position && props.position == MenuPosition.ABSOLUTE ? <MenuCloseButton onClick={() => toggleSidemenu()} /> : <HamburgerIcon onClick={() => toggleSidemenu()} />}</div>
        {props.settings?.enabeLogoSideMenu == true ? (
          <div className="brand-logo">
            {props.global && (
              <NavLink className={"linkCss"} exact={true} to="/">
                <BrandLogo global={props.global} />{" "}
              </NavLink>
            )}
          </div>
        ) : (
          <div></div>
        )}
      </div>
      <div className="content">
        {userLoaded && <SideMenuTopContent sidemenuTopContent={props.content && props.content.menuTopContent} />}
        <SideMenuComponents position={props.position} onClose={toggleSidemenu} component={props.settings?.menuComponents} />
      </div>
      <div className="menu-items-container">
        <div className="menu-items menu-items-fixed">
          {props.mainItems &&
            props.mainItems
              .map((rawMenuItem) => mapMenuItem(rawMenuItem, props.locale ?? "en", location.pathname))
              .map((processedMenuItem) => {
                if (processedMenuItem) {
                  return (
                    <>
                      {processedMenuItem.external && !processedMenuItem.subMenu && (
                        <BrandA className={"nav-link submenu-link "} href={processedMenuItem.url} key={processedMenuItem.name} target={processedMenuItem.target} onClick={() => (isAbsolute ? toggleSidemenu() : undefined)}>
                          {processedMenuItem.icon?.className && <i className={"" + processedMenuItem?.icon?.className}></i>}
                          {processedMenuItem.text}
                        </BrandA>
                      )}
                      {!processedMenuItem.external && !processedMenuItem.subMenu && (
                        <NavLink
                          className={"nav-link submenu-link " + processedMenuItem.className}
                          to={processedMenuItem.url}
                          key={processedMenuItem.name}
                          isActive={(match, location) => isCasinoMenuItemActive(match, location, processedMenuItem)}
                          activeClassName={"active"}
                          onClick={() => (isAbsolute ? toggleSidemenu() : undefined)}
                        >
                          {processedMenuItem.icon?.className && <i className={"" + processedMenuItem.icon.className}></i>}
                          {processedMenuItem.text}
                        </NavLink>
                      )}
                      {processedMenuItem.subMenu && <Submenu key={processedMenuItem.url} menuItem={processedMenuItem} close={() => (isAbsolute ? toggleSidemenu() : undefined)} />}
                    </>
                  );
                }
              })}
        </div>
      </div>
      <div className="menu-divider"></div>
      <div className={"footer-items footer-items-fixed"}>
        {props.footerItems &&
          props.footerItems
            .map((rawMenuItem) => mapMenuItem(rawMenuItem, props.locale ?? "en", location.pathname))
            .map((processedMenuItem) => {
              if (processedMenuItem) {
                if (processedMenuItem.supportLink) {
                  return (
                    <NavLink
                      key={processedMenuItem.name}
                      className={"nav-link submenu-link support-link"}
                      to={"#"}
                      onClick={() => {
                        openFreshChat();
                        if (isAbsolute) {
                          toggleSidemenu();
                        }
                      }}
                    >
                      {processedMenuItem.icon?.className && <i className={"" + processedMenuItem?.icon?.className}></i>}
                      {processedMenuItem.text}
                    </NavLink>
                  );
                }
                return (
                  <>
                    {processedMenuItem.external && !processedMenuItem.subMenu && (
                      <BrandA className={"nav-link submenu-link"} href={processedMenuItem.url} key={processedMenuItem.name} target={processedMenuItem.target} onClick={() => (isAbsolute ? toggleSidemenu() : undefined)}>
                        {processedMenuItem.icon?.className && <i className={"" + processedMenuItem?.icon?.className}></i>}
                        {processedMenuItem.text}
                      </BrandA>
                    )}
                    {!processedMenuItem.external && !processedMenuItem.subMenu && (
                      <NavLink
                        className={"nav-link submenu-link " + processedMenuItem.className}
                        to={processedMenuItem.url}
                        key={processedMenuItem.name}
                        isActive={(match, location) => isCasinoMenuItemActive(match, location, processedMenuItem)}
                        activeClassName={"active"}
                        onClick={() => (isAbsolute ? toggleSidemenu() : undefined)}
                      >
                        {processedMenuItem.icon?.className && <i className={"" + processedMenuItem.icon.className}></i>}
                        {processedMenuItem.text}
                      </NavLink>
                    )}
                  </>
                );
              }
            })}
      </div>
      <SideMenuLanguage position={props.position} isExpanded={props.isExpanded} />
      <div className="footer-content footer-content-fixed">
        <SideMenuBottomContent sidemenuBottomContent={props.content?.menuBottomContent} />
        <div className={"app-install-container "}>
          <AppInstallComponent />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------------ */
/* ------------------------------------------------------------------------------------------------------ */

function CollapsedSidemenuContent(props: SidemenuContent) {
  const location = useLocation();
  const dispatch = useDispatch();

  const toggleSidemenu = () => {
    dispatch(showSideMenu(!props.isExpanded));
  };
  return (
    <div className={`sidemenu-container sidemenu-container-collapsed ${!props.isExpanded ? "active" : ""}`}>
      <div className={`sidemenu-header collapsed`}>
        <div className={`close-button collapsed ` + props.position?.toLocaleLowerCase()}>
          <HamburgerIcon onClick={() => toggleSidemenu()} />
        </div>
      </div>
      <div className="content collapsed">
        {props.content?.menuTopContent &&
          !!props.content?.menuTopContent.length &&
          props.content?.menuTopContent.map((content, i) => {
            if (!content.collapsedMode) return;
            if ((content.loggedIn && props.isLoggedIn) || (!content.loggedIn && !props.isLoggedIn)) return <SideMenuContent key={i} content={content} isLoggedIn={props.isLoggedIn} classname="menu-top-content-collapsed" />;
          })}
      </div>
      <div className="menu-items menu-items-collapsed">
        {props.mainItems &&
          props.mainItems
            .map((rawMenuItem) => mapMenuItem(rawMenuItem, props.locale ?? "en", location.pathname))
            .map((processedMenuItem) => {
              if (processedMenuItem) {
                return (
                  <>
                    {processedMenuItem.external && processedMenuItem.icon?.className && (
                      <CopyPopOver key={processedMenuItem.name} info={processedMenuItem.text}>
                        <BrandA className={"nav-link fixed-nav-link "} href={processedMenuItem.url} key={processedMenuItem.url} target={processedMenuItem.target}>
                          {processedMenuItem.icon?.className && <i className={" " + processedMenuItem?.icon?.className}></i>}
                        </BrandA>
                      </CopyPopOver>
                    )}
                    {!processedMenuItem.external && processedMenuItem.icon?.className && (
                      <CopyPopOver key={processedMenuItem.name} info={processedMenuItem.text}>
                        <NavLink className={"nav-link fixed-nav-link " + processedMenuItem.className} to={processedMenuItem.url ?? ""} key={processedMenuItem.name} isActive={(match, location) => isCasinoMenuItemActive(match, location, processedMenuItem)} activeClassName={"active-collapsed"}>
                          {processedMenuItem.icon?.className && <i className={" " + processedMenuItem.icon.className}></i>}
                        </NavLink>
                      </CopyPopOver>
                    )}
                  </>
                );
              }
            })}
      </div>
      <div className="menu-divider"></div>
      <div className={"footer-items footer-items-collapsed"}>
        {props.footerItems &&
          props.footerItems
            .map((rawMenuItem) => mapMenuItem(rawMenuItem, props.locale ?? "en", location.pathname))
            .map((processedMenuItem) => {
              if (processedMenuItem?.disableSidebarCollapse) return;
              if (processedMenuItem && processedMenuItem.icon?.className) {
                if (processedMenuItem.supportLink) {
                  return (
                    <CopyPopOver key={processedMenuItem.name} info={processedMenuItem.text}>
                      <NavLink
                        key={processedMenuItem.name}
                        className={"nav-link support-link"}
                        to={"#"}
                        onClick={() => {
                          openFreshChat();
                        }}
                      >
                        {processedMenuItem.icon?.className && <i className={"" + processedMenuItem?.icon?.className}></i>}
                      </NavLink>
                    </CopyPopOver>
                  );
                }
                return (
                  <>
                    {processedMenuItem.external && !processedMenuItem.subMenu && (
                      <CopyPopOver key={processedMenuItem.name} info={processedMenuItem.text}>
                        <BrandA className={"nav-link submenu-link"} href={processedMenuItem.url} key={processedMenuItem.url} target={processedMenuItem.target}>
                          {processedMenuItem.icon?.className && <i className={"" + processedMenuItem?.icon?.className}></i>}
                        </BrandA>
                      </CopyPopOver>
                    )}
                    {!processedMenuItem.external && !processedMenuItem.subMenu && (
                      <CopyPopOver key={processedMenuItem.name} info={processedMenuItem.text}>
                        <NavLink className={"nav-link submenu-link expandable " + processedMenuItem.className} to={processedMenuItem.url} key={processedMenuItem.url} isActive={(match, location) => isCasinoMenuItemActive(match, location, processedMenuItem)} activeClassName={"active-collapsed"}>
                          {processedMenuItem.icon?.className && <i className={"" + processedMenuItem.icon.className}></i>}
                        </NavLink>
                      </CopyPopOver>
                    )}
                  </>
                );
              }
            })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------------ */
/* ------------------------------------------------------------------------------------------------------ */
